<template>
  <div class="gs-player-section">
    <gs-field label="Title" :error="nameError">
      <input
        data-field="name"
        type="text"
        class="gs-player-section__input"
        :class="{ 'gs-player-section__input--error': nameError }"
        :value="model.name"
        @input="setValue('name', $event.target.value)"
      />
    </gs-field>

    <gs-field
      :label="$t('frontend_groovevideo_videos_step1_category')"
      hint="Categories group videos in your library. Viewers never see them."
    >
      <treeselect
        v-model="categoryId"
        class="gs-player-section__treeselect"
        :multiple="false"
        :options="categories"
        :close-on-select="true"
        :clearable="false"
        :clear-on-select="true"
        :default-expand-level="1"
        :show-count="true"
        :max-height="200"
        :normalizer="normalizer"
      />
      <button type="button" class="gs-player-section__link" @click="openNewCategory()">
        + New category
      </button>
    </gs-field>

    <gs-field
      :label="$t('frontend_groovevideo_videos_step1_description')"
      hint="A note for yourself. It is not shown on the player."
    >
      <textarea
        data-field="description"
        rows="3"
        class="gs-player-section__input"
        :value="model.description"
        @input="setValue('description', $event.target.value)"
      ></textarea>
    </gs-field>

    <source-row :model="model" @replaced="$emit('replaced')" />

    <add-new-category
      v-if="newCategoryOpen"
      ref="addNewCategory"
      class="text-left"
      :categories="categories"
      @close-modal="newCategoryOpen = false"
      @on-save="saveCategory($event)"
    />
  </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import { required } from 'vuelidate/lib/validators';
import AddNewCategory from '@/pages/videos/AddNewCategory';
import SourceRow from './SourceRow.vue';

export default {
  name: 'DetailsSection',

  components: { Treeselect, AddNewCategory, SourceRow },

  props: {
    model: {
      type: Object,
      required: true,
    },
    categories: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      newCategoryOpen: false,
    };
  },

  validations: {
    model: {
      name: {
        required,
      },
    },
  },

  computed: {
    categoryId: {
      get() {
        return this.model.category_id;
      },
      set(value) {
        this.setValue('category_id', value);
      },
    },

    nameError() {
      return this.$v.model.name.$error ? 'Give your video a name.' : '';
    },
  },

  methods: {
    // Ported from step1: the tree comes back as `category` / `child`.
    normalizer(node) {
      return {
        id: node.id,
        label: node.category,
        children: node.child && node.child.length ? node.child : undefined,
        user_id: node.user_id,
      };
    },

    setValue(key, value) {
      this.model[key] = value;
      this.$emit('updateModel', this.model);
    },

    openNewCategory() {
      this.newCategoryOpen = true;
    },

    // The modal saves the category itself and answers with its id; the panel
    // owns the list, so it reloads it and selects the new one.
    async saveCategory(categoryId) {
      if (this.$refs.addNewCategory) {
        await this.$refs.addNewCategory.closeCategoryModal();
      }
      this.$emit('categoryCreated', categoryId);
    },

    validate() {
      this.$v.$touch();
      const isValid = !this.$v.$invalid;
      this.$emit('on-validate', isValid);
      return isValid;
    },
  },
};
</script>

<style scoped>
.gs-player-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 14px;
}

.gs-player-section__input {
  display: block;
  width: 100%;
  min-height: 40px;
  padding: 10px 12px;
  color: var(--gs-foreground);
  background: var(--gs-input);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  font-size: 14px;
  line-height: 1.4;
}

textarea.gs-player-section__input {
  min-height: 88px;
  resize: vertical;
}

.gs-player-section__input:focus {
  border-color: var(--gs-primary);
  outline: none;
  box-shadow: var(--gs-primary-shadow);
}

.gs-player-section__input--error {
  border-color: var(--gs-danger);
}

.gs-player-section__link {
  align-self: flex-start;
  padding: 0;
  margin-top: 8px;
  color: var(--gs-primary);
  background: none;
  border: 0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

/* The treeselect keeps its own chrome; only its box is aligned with the kit. */
.gs-player-section ::v-deep .vue-treeselect__control {
  min-height: 40px;
  background: var(--gs-input);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}
</style>
